import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
    supportDiagnose,
    createSupportTicket,
    restorePurchase,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
    HelpCircle,
    Upload,
    CreditCard,
    Lock,
    AlertTriangle,
    DollarSign,
    User,
    RefreshCcw,
    FileWarning,
    Search,
    Trash2,
    CheckCircle2,
    LifeBuoy,
} from "lucide-react";

// ──────────────────────────────────────────────────────────────────────
// Self-service topic library (the user's pasted spec, condensed into
// plain-English answers that resolve most tickets before they're filed.)
// ──────────────────────────────────────────────────────────────────────
const TOPICS = [
    {
        id: "upload_failed",
        icon: Upload,
        title: "I can't upload my report",
        body: "Make sure your file is a PDF (not a screenshot or photo) and under 50MB. If your inspector sent a scanned PDF, give it a few extra minutes — OCR runs in the background.",
    },
    {
        id: "still_processing",
        icon: RefreshCcw,
        title: "My report is still processing",
        body: "Large scanned PDFs can take several minutes. You can safely leave this page and return from your dashboard — we'll save your spot.",
    },
    {
        id: "paid_locked",
        icon: Lock,
        title: "My payment went through but my report is locked",
        body: "This usually happens when Stripe's confirmation took a moment to land. Use the 'Restore Purchase' button below — we'll recheck Stripe and unlock your report automatically.",
    },
    {
        id: "refund",
        icon: DollarSign,
        title: "I need a refund",
        body: "We handle refunds promptly when a report failed to generate or you were charged twice. Submit a request below with the report ID and we'll review within 1–2 business days.",
    },
    {
        id: "delete_report",
        icon: Trash2,
        title: "I want to delete a report",
        body: "Open the report → Settings → Delete report. Deletion is reversible from the archive for 30 days, then permanent.",
    },
    {
        id: "delete_account",
        icon: User,
        title: "I want to delete my account",
        body: "Visit Account → Privacy & deletion → Request deletion. We'll send a confirmation email and remove eligible data per our retention policy.",
    },
    {
        id: "forgot_password",
        icon: Lock,
        title: "I forgot my password",
        body: "Visit Forgot password and enter your email. We'll send a secure reset link.",
        cta: { to: "/forgot-password", label: "Reset password" },
    },
    {
        id: "forgot_email",
        icon: Search,
        title: "I forgot which email I used",
        body: "Use Recover your account and we'll quietly check possible emails for you — for privacy, we won't say on-screen which one matched.",
        cta: { to: "/recover-account", label: "Recover account" },
    },
    {
        id: "share",
        icon: HelpCircle,
        title: "I want to share my report",
        body: "Open the report → Share. We generate a tokenized link you can send directly. Sharing never exposes your email or payment details.",
    },
    {
        id: "exposure_off",
        icon: AlertTriangle,
        title: "My repair exposure range seems off",
        body: "Repair ranges are planning estimates that vary by scope, local labor, access, and contractor minimums. Submit a 'Report accuracy' ticket below with what you expected and we'll use it to improve calibration.",
    },
    {
        id: "quote_help",
        icon: FileWarning,
        title: "I need help with a quote request",
        body: "If a contractor hasn't reached out within 2 business days, submit a 'Quote request issue' below — we'll re-route to a different partner.",
    },
    {
        id: "report_failed",
        icon: FileWarning,
        title: "My report failed to generate",
        body: "Your payment is preserved — we'll retry generation without charging you again. Submit a ticket below and we'll investigate.",
    },
    {
        id: "billing",
        icon: CreditCard,
        title: "I need help with billing",
        body: "Visit Account → Billing to view invoices and update your payment method. For subscription changes, use the Stripe billing portal link there.",
    },
    {
        id: "subscription",
        icon: CreditCard,
        title: "I want to cancel or manage my subscription",
        body: "Account → Billing → Manage subscription opens your Stripe portal where you can pause, cancel, or change plans.",
    },
];

// Structured ticket categories + their follow-up fields
const CATEGORIES = [
    {
        id: "upload",
        label: "Upload issue",
        icon: Upload,
        prompts: ["What happened?", "Any error message visible?"],
    },
    {
        id: "payment_billing",
        label: "Payment / billing",
        icon: CreditCard,
        prompts: ["Checkout date (if known)?", "What happened?"],
    },
    {
        id: "report_access",
        label: "Report access",
        icon: Lock,
        prompts: ["Which report?", "What's blocking you?"],
    },
    {
        id: "report_accuracy",
        label: "Report accuracy concern",
        icon: AlertTriangle,
        prompts: ["What feels inaccurate?", "Property ZIP (helps calibration)"],
    },
    {
        id: "repair_exposure",
        label: "Repair exposure concern",
        icon: DollarSign,
        prompts: [
            "Which issue card?",
            "What range did you expect locally?",
        ],
    },
    {
        id: "account_login",
        label: "Account / login",
        icon: User,
        prompts: ["What's stopping you from signing in?"],
    },
    {
        id: "refund",
        label: "Refund request",
        icon: DollarSign,
        prompts: ["Reason for refund?", "Duplicate charge or failed report?"],
    },
    {
        id: "quote_request",
        label: "Quote request issue",
        icon: FileWarning,
        prompts: ["Which quote?", "What went wrong?"],
    },
    {
        id: "privacy_deletion",
        label: "Privacy / account deletion",
        icon: Trash2,
        prompts: ["Tell us briefly what you'd like removed"],
    },
    {
        id: "subscription",
        label: "Subscription",
        icon: CreditCard,
        prompts: ["What would you like changed?"],
    },
    {
        id: "other",
        label: "Other",
        icon: HelpCircle,
        prompts: ["Tell us what's going on"],
    },
];

function Hint({ hint, onAction }) {
    if (!hint) return null;
    return (
        <div
            className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-5"
            data-testid="support-diagnostic-hint"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-amber-900">
                        {hint.title}
                    </div>
                    <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                        {hint.message}
                    </p>
                    {hint.action === "restore_purchase" && (
                        <button
                            type="button"
                            onClick={onAction}
                            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-900 text-white text-sm hover:bg-amber-800"
                            data-testid="support-restore-purchase-btn"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            Restore Purchase
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function TopicCard({ topic }) {
    const Icon = topic.icon;
    return (
        <div
            className="bl-card p-5 hover:border-bl-primary/40 transition-colors"
            data-testid={`support-topic-${topic.id}`}
        >
            <div className="flex items-start gap-3">
                <Icon className="w-5 h-5 text-bl-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold text-bl">
                        {topic.title}
                    </h3>
                    <p className="mt-2 text-sm text-bl-muted leading-relaxed">
                        {topic.body}
                    </p>
                    {topic.cta && (
                        <Link
                            to={topic.cta.to}
                            className="mt-3 inline-flex items-center gap-1 text-sm text-bl-primary hover:underline"
                        >
                            {topic.cta.label} →
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function SupportCenter() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState(
        searchParams.get("category") || "",
    );
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [email, setEmail] = useState(user?.email || "");
    const [hint, setHint] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);

    const reportId = searchParams.get("report_id");
    const uploadId = searchParams.get("upload_id");
    const issueId = searchParams.get("issue_id");
    const diagnoseDoneRef = useRef(false);

    // Track Support Center view + run diagnostic preflight when we have context
    useEffect(() => {
        track("support_center_viewed", {
            has_context: !!(reportId || uploadId),
        });
    }, [reportId, uploadId]);

    useEffect(() => {
        if (diagnoseDoneRef.current) return;
        if (!user && !reportId && !uploadId) return;
        diagnoseDoneRef.current = true;
        supportDiagnose({ report_id: reportId, upload_id: uploadId })
            .then((r) => {
                setSnapshot(r.snapshot);
                setHint(r.hint);
                if (r.hint) {
                    track("support_auto_diagnosis_shown", {
                        code: r.hint.code,
                    });
                }
            })
            .catch(() => {});
    }, [user, reportId, uploadId]);

    const handleRestore = async () => {
        try {
            track("restore_purchase_clicked");
            const r = await restorePurchase({
                upload_id: uploadId,
                report_id: reportId,
            });
            if (r.restored || r.already_entitled) {
                track("restore_purchase_succeeded", { tier: r.tier });
                toast.success(
                    r.already_entitled
                        ? "Your report is already unlocked. Try refreshing."
                        : "Access restored. Opening your report…",
                );
                if (reportId) {
                    setTimeout(() => navigate(`/report/${reportId}`), 1200);
                } else {
                    setHint(null);
                }
            } else {
                track("restore_purchase_failed");
                toast.error(r.message || "Could not auto-restore. Please submit a ticket below.");
            }
        } catch (e) {
            track("restore_purchase_failed");
            toast.error(
                e?.response?.data?.detail ||
                    "Could not check your payment. Please submit a ticket.",
            );
        }
    };

    const visibleTopics = useMemo(() => {
        if (!search.trim()) return TOPICS;
        const q = search.toLowerCase();
        return TOPICS.filter(
            (t) =>
                t.title.toLowerCase().includes(q) ||
                t.body.toLowerCase().includes(q),
        );
    }, [search]);

    const categoryInfo = CATEGORIES.find((c) => c.id === category);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!category) {
            toast.error("Please pick a category.");
            return;
        }
        if (!user && !email.trim()) {
            toast.error("Please provide your email.");
            return;
        }
        if (subject.trim().length < 3 || description.trim().length < 10) {
            toast.error("Add a few more details so we can help.");
            return;
        }
        setSubmitting(true);
        track("support_ticket_submitted", { category });
        try {
            const ticket = await createSupportTicket({
                category,
                subject: subject.trim(),
                description: description.trim(),
                email: user ? undefined : email.trim().toLowerCase(),
                related_report_id: reportId || undefined,
                related_upload_id: uploadId || undefined,
                related_issue_id: issueId || undefined,
            });
            setSubmitted(ticket);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not submit your ticket.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <PublicLayout>
                <section className="bl-paper">
                    <div
                        className="bl-container py-16 max-w-2xl text-center"
                        data-testid="support-success-page"
                    >
                        <div className="mx-auto w-14 h-14 rounded-full bg-bl-primary/10 flex items-center justify-center">
                            <CheckCircle2 className="w-7 h-7 text-bl-primary" />
                        </div>
                        <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                            We've got your message.
                        </h1>
                        <p className="mt-4 text-bl-muted leading-relaxed">
                            We typically respond within 1–2 business days.
                            Payment or report access issues are prioritized.
                        </p>
                        <p className="mt-6 text-sm text-bl-muted">
                            Reference:{" "}
                            <span
                                className="font-mono-bl text-bl"
                                data-testid="support-ticket-ref"
                            >
                                {submitted.ticket_id}
                            </span>
                        </p>
                        <div className="mt-8 flex justify-center gap-3">
                            <Link to="/dashboard" className="bl-btn-primary">
                                Back to dashboard
                            </Link>
                        </div>
                    </div>
                </section>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-14 max-w-5xl"
                    data-testid="support-center-page"
                >
                    <div className="flex items-center gap-2 text-bl-primary">
                        <LifeBuoy className="w-5 h-5" />
                        <span className="bl-eyebrow">Help center</span>
                    </div>
                    <h1 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight text-bl leading-[1.05]">
                        How can we{" "}
                        <span className="font-italic-accent text-bl-primary">
                            help
                        </span>
                        ?
                    </h1>
                    <p className="mt-5 max-w-2xl text-bl-muted leading-relaxed">
                        Most questions are answered below. If you still need a
                        human, the form at the bottom routes straight to our
                        team.{" "}
                        <span className="text-bl">
                            We typically respond within 1–2 business days.
                        </span>{" "}
                        Payment and report access issues are prioritized.
                    </p>

                    {/* Context-aware auto-diagnosis */}
                    <Hint hint={hint} onAction={handleRestore} />

                    {/* Self-service topic library */}
                    <div className="mt-12">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <div className="bl-eyebrow">Quick answers</div>
                                <h2 className="font-display font-semibold text-2xl text-bl mt-1">
                                    Common topics
                                </h2>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bl-muted" />
                                <input
                                    type="search"
                                    placeholder="Search topics…"
                                    value={search}
                                    onChange={(e) =>
                                        setSearch(e.target.value)
                                    }
                                    className="pl-9 pr-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary w-64"
                                    data-testid="support-search-input"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {visibleTopics.map((t) => (
                                <TopicCard key={t.id} topic={t} />
                            ))}
                            {visibleTopics.length === 0 && (
                                <p className="text-bl-muted text-sm">
                                    No topics match — try the form below.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Structured ticket form */}
                    <div className="mt-16" id="contact-form">
                        <div className="bl-eyebrow">Still need help?</div>
                        <h2 className="font-display font-semibold text-2xl text-bl mt-1 mb-5">
                            Send us a message
                        </h2>
                        <form
                            onSubmit={handleSubmit}
                            className="bl-card p-6 md:p-7 space-y-5"
                            data-testid="support-ticket-form"
                        >
                            {!user && (
                                <label className="block">
                                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                        Your email
                                    </div>
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                        data-testid="support-email-input"
                                    />
                                </label>
                            )}

                            <div>
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Category
                                </div>
                                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {CATEGORIES.map((c) => {
                                        const Icon = c.icon;
                                        const active = category === c.id;
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => {
                                                    setCategory(c.id);
                                                    track(
                                                        "support_topic_selected",
                                                        { category: c.id },
                                                    );
                                                }}
                                                className={
                                                    "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors " +
                                                    (active
                                                        ? "bg-bl-primary text-bl-surface border-bl-primary"
                                                        : "border-bl text-bl-muted hover:border-bl-primary hover:text-bl")
                                                }
                                                data-testid={`support-category-${c.id}`}
                                            >
                                                <Icon className="w-4 h-4 flex-shrink-0" />
                                                <span className="truncate">
                                                    {c.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <label className="block">
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Subject
                                </div>
                                <input
                                    type="text"
                                    required
                                    minLength={3}
                                    maxLength={200}
                                    value={subject}
                                    onChange={(e) =>
                                        setSubject(e.target.value)
                                    }
                                    placeholder={
                                        categoryInfo
                                            ? `Briefly: ${categoryInfo.label.toLowerCase()}`
                                            : "Briefly summarize the issue"
                                    }
                                    className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                    data-testid="support-subject-input"
                                />
                            </label>

                            <label className="block">
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Details
                                </div>
                                <textarea
                                    required
                                    minLength={10}
                                    maxLength={5000}
                                    rows={6}
                                    value={description}
                                    onChange={(e) =>
                                        setDescription(e.target.value)
                                    }
                                    placeholder={
                                        categoryInfo
                                            ? categoryInfo.prompts.join(" • ")
                                            : "Tell us what's going on"
                                    }
                                    className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary resize-none"
                                    data-testid="support-description-input"
                                />
                            </label>

                            {(reportId || uploadId) && (
                                <div className="text-xs text-bl-muted bg-bl-bg/40 border border-bl rounded-md p-3">
                                    <strong className="text-bl">
                                        Context auto-attached:
                                    </strong>{" "}
                                    {reportId && (
                                        <span className="font-mono-bl ml-1">
                                            report {reportId.slice(0, 12)}…
                                        </span>
                                    )}
                                    {uploadId && (
                                        <span className="font-mono-bl ml-2">
                                            upload {uploadId.slice(0, 12)}…
                                        </span>
                                    )}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting || !category}
                                className="bl-btn-primary w-full md:w-auto"
                                data-testid="support-submit-btn"
                            >
                                {submitting ? "Sending…" : "Send message"}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
